// Depo Nakil Fişi sayfası — kaynak depodan hedef depoya stok aktarımı.
// Satış faturası kalem stili (büyük stok kartı + birim/miktar) + header'da personel + özel kod1/2.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, ListPageHeader } from '../components/ui';
import { CardHero, CardStatusBadge, CardSplitButton } from '../components/card';
import DataTable from '../components/DataTable';
import SearchableSelect from '../components/SearchableSelect';
import { fmtAmount } from '../utils/numberFormat';

const fmt = (n) => fmtAmount(n);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '';
const today = () => new Date().toISOString().slice(0, 10);

const emptyLine = () => ({
  stock_id: '', stock_code: '', stock_name: '', barcode: '',
  color_name: '', size_name: '',
  quantity: 1, quantity_base: 1,
  unit_id: null, unit_type: 'unit1', units: [],
  lot_id: null, description: '',
  available: null,
});

const emptyHeader = () => ({
  transfer_date: today(), from_warehouse_id: '', to_warehouse_id: '',
  description: '', vehicle_plate: '', driver_name: '', expected_arrival: '', notes: '',
  personnel_id: '', special_code1: '', special_code2: '',
});

export default function WarehouseTransfersPage({ autoOpenId } = {}) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [lookups, setLookups] = useState({ stocks: [], warehouses: [], personnel: [], specialCodes1: [], specialCodes2: [], settings: {} });

  // Renk/Beden takibi ayarı (settings.color_size_tracking)
  const colorSizeEnabled = (() => {
    const v = lookups.settings?.color_size_tracking;
    if (v === true || v === 1) return true;
    return String(v || '').toLowerCase() === 'true';
  })();

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('add');
  const [selected, setSelected] = useState(null);
  const [header, setHeader] = useState(emptyHeader());
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [nextNo, setNextNo] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [showMultiStock, setShowMultiStock] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/warehouse-transfers', { params: { search } });
      if (r.data.success) setList(r.data.data.items);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [search]);

  const fetchLookups = useCallback(async () => {
    try {
      const r = await api.get('/warehouse-transfers/lookups/all');
      if (r.data.success) setLookups(r.data.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchList(); fetchLookups(); }, [fetchList, fetchLookups]);
  useEffect(() => { if (autoOpenId) openView(autoOpenId); }, [autoOpenId]); // eslint-disable-line

  const getStockUnits = (stockId) => {
    const stk = lookups.stocks.find(s => s.id === stockId);
    if (!stk) return [];
    const u = [];
    if (stk.unit_id)  u.push({ id: stk.unit_id,  code: stk.unit_code,  name: stk.unit_name,  type: 'unit1', ratio: 1 });
    if (stk.unit2_id) u.push({ id: stk.unit2_id, code: stk.unit2_code, name: stk.unit2_name, type: 'unit2', ratio: parseFloat(stk.unit2_ratio) || 1 });
    if (stk.unit3_id) u.push({ id: stk.unit3_id, code: stk.unit3_code, name: stk.unit3_name, type: 'unit3', ratio: parseFloat(stk.unit3_ratio) || 1 });
    return u;
  };

  const openAdd = async () => {
    if ((lookups.warehouses || []).length < 2) {
      alert('En az 2 aktif depo tanımlı olmalı.');
      return;
    }
    setMode('add');
    setSelected(null);
    setHeader(emptyHeader());
    setLines([emptyLine()]);
    setMsg(null);
    setNextNo(null);
    setBarcodeInput('');
    setShowModal(true);
    // Sonraki belge numarasını al
    try {
      const r = await api.get('/warehouse-transfers/lookup/next-no');
      if (r.data.success) setNextNo(r.data.data.transfer_no);
    } catch {}
  };

  const openView = async (id) => {
    try {
      const r = await api.get('/warehouse-transfers/' + id);
      if (r.data.success) {
        setMode('view');
        setSelected(r.data.data);
        setShowModal(true);
      }
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || 'Yüklenemedi' }); }
  };

  const openEdit = () => {
    if (!selected) return;
    setHeader({
      transfer_date: selected.transfer_date ? selected.transfer_date.slice(0, 10) : today(),
      from_warehouse_id: selected.from_warehouse_id || '',
      to_warehouse_id: selected.to_warehouse_id || '',
      description: selected.description || '',
      vehicle_plate: selected.vehicle_plate || '',
      driver_name: selected.driver_name || '',
      expected_arrival: selected.expected_arrival ? selected.expected_arrival.slice(0, 10) : '',
      notes: selected.notes || '',
      personnel_id: selected.personnel_id || '',
      special_code1: selected.special_code1 || '',
      special_code2: selected.special_code2 || '',
    });
    const ls = (selected.lines || []).map(l => {
      const stk = lookups.stocks.find(s => s.id === l.stock_id);
      const units = [];
      if (stk?.unit_id)  units.push({ id: stk.unit_id,  code: stk.unit_code,  name: stk.unit_name,  type: 'unit1', ratio: 1 });
      if (stk?.unit2_id) units.push({ id: stk.unit2_id, code: stk.unit2_code, name: stk.unit2_name, type: 'unit2', ratio: parseFloat(stk.unit2_ratio) || 1 });
      if (stk?.unit3_id) units.push({ id: stk.unit3_id, code: stk.unit3_code, name: stk.unit3_name, type: 'unit3', ratio: parseFloat(stk.unit3_ratio) || 1 });
      return {
        stock_id: l.stock_id,
        stock_code: l.stock_code || stk?.code || '',
        stock_name: l.stock_name || stk?.name || '',
        barcode: l.stock_barcode || stk?.barcode || '',
        color_name: stk?.color_name || '',
        size_name: stk?.size_name || '',
        quantity: l.quantity, quantity_base: l.quantity_base || l.quantity,
        unit_id: l.unit_id, unit_type: l.unit_type || 'unit1',
        units,
        lot_id: l.lot_id || null,
        description: l.description || '',
        available: null,
      };
    });
    setLines(ls.length ? ls : [emptyLine()]);
    setMsg(null);
    setMode('edit');
  };

  const fetchAvailable = async (warehouseId, stockId, idx) => {
    if (!warehouseId || !stockId) return;
    try {
      const r = await api.get(`/warehouse-transfers/lookup/inventory/${warehouseId}/${stockId}`);
      setLines(prev => {
        const nl = [...prev];
        if (nl[idx]) nl[idx].available = r.data.data?.quantity ?? 0;
        return nl;
      });
    } catch {}
  };

  // Kalem güncelleme — sales invoice'taki updateLine pattern'ı
  const updateLine = (idx, key, val) => {
    setLines(prev => {
      const nl = [...prev];
      const ln = { ...nl[idx], [key]: val };

      if (key === 'stock_id') {
        const stk = lookups.stocks.find(s => s.id === val);
        if (stk) {
          ln.stock_code = stk.code;
          ln.stock_name = stk.name;
          ln.barcode = stk.barcode || '';
          ln.color_name = stk.color_name || '';
          ln.size_name = stk.size_name || '';
          ln.unit_id = stk.unit_id;
          ln.unit_type = 'unit1';
          ln.units = getStockUnits(val);
          ln.quantity_base = parseFloat(ln.quantity) || 1;
          ln.available = null;
          if (header.from_warehouse_id) fetchAvailable(header.from_warehouse_id, val, idx);
        } else {
          ln.stock_code = ''; ln.stock_name = ''; ln.barcode = '';
          ln.color_name = ''; ln.size_name = '';
          ln.units = []; ln.unit_id = null; ln.unit_type = 'unit1';
          ln.available = null;
        }
      }
      if (key === 'unit_type') {
        const u = (ln.units || []).find(x => x.type === val);
        if (u) {
          ln.unit_id = u.id;
          const qty = parseFloat(ln.quantity) || 0;
          ln.quantity_base = qty * (u.ratio || 1);
        }
      }
      if (key === 'quantity') {
        const qty = parseFloat(val) || 0;
        const u = (ln.units || []).find(x => x.type === ln.unit_type);
        ln.quantity_base = qty * (u?.ratio || 1);
      }
      nl[idx] = ln;
      return nl;
    });
  };

  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (idx) => setLines(prev => prev.filter((_, i) => i !== idx));

  // Barkod okut: stok bul + boş satıra yerleştir veya yeni satır ekle
  const handleBarcodeScan = async () => {
    const code = (barcodeInput || '').trim();
    if (!code) return;
    try {
      const r = await api.get('/warehouse-transfers/lookup/stock-by-barcode/' + encodeURIComponent(code));
      const stk = r.data.data;
      if (!stk) return;
      setLines(prev => {
        // Aynı stok varsa miktarını +1
        const existIdx = prev.findIndex(l => l.stock_id === stk.id);
        if (existIdx >= 0) {
          const nl = [...prev];
          const qty = (parseFloat(nl[existIdx].quantity) || 0) + 1;
          const u = (nl[existIdx].units || []).find(x => x.type === nl[existIdx].unit_type);
          nl[existIdx] = { ...nl[existIdx], quantity: qty, quantity_base: qty * (u?.ratio || 1) };
          return nl;
        }
        // Boş slot bul ya da yeni ekle
        const emptyIdx = prev.findIndex(l => !l.stock_id);
        const units = [];
        if (stk.unit_id)  units.push({ id: stk.unit_id,  code: stk.unit_code,  name: stk.unit_name,  type: 'unit1', ratio: 1 });
        if (stk.unit2_id) units.push({ id: stk.unit2_id, code: stk.unit2_code, name: stk.unit2_name, type: 'unit2', ratio: parseFloat(stk.unit2_ratio) || 1 });
        if (stk.unit3_id) units.push({ id: stk.unit3_id, code: stk.unit3_code, name: stk.unit3_name, type: 'unit3', ratio: parseFloat(stk.unit3_ratio) || 1 });
        const newLine = {
          ...emptyLine(),
          stock_id: stk.id, stock_code: stk.code, stock_name: stk.name, barcode: stk.barcode || '',
          color_name: stk.color_name || '', size_name: stk.size_name || '',
          unit_id: stk.unit_id, unit_type: 'unit1', units,
          quantity: 1, quantity_base: 1,
        };
        if (emptyIdx >= 0) {
          const nl = [...prev]; nl[emptyIdx] = newLine; return nl;
        }
        return [...prev, newLine];
      });
      setBarcodeInput('');
      // Mevcut'u tetikle
      if (header.from_warehouse_id) {
        setTimeout(() => {
          setLines(prev => {
            prev.forEach((l, i) => { if (l.stock_id === stk.id) fetchAvailable(header.from_warehouse_id, stk.id, i); });
            return prev;
          });
        }, 0);
      }
    } catch (e) {
      setMsg({ type: 'error', text: 'Barkod bulunamadı: ' + code });
      setTimeout(() => setMsg(null), 2500);
    }
  };

  // Çoklu Stok Seç: seçili stokları toplu satır olarak ekle
  const addMultipleStocks = (stockIds) => {
    if (!stockIds?.length) return;
    setLines(prev => {
      const existing = new Set(prev.filter(l => l.stock_id).map(l => l.stock_id));
      const toAdd = stockIds.filter(id => !existing.has(id));
      const newLines = toAdd.map(id => {
        const stk = lookups.stocks.find(s => s.id === id);
        if (!stk) return null;
        const units = [];
        if (stk.unit_id)  units.push({ id: stk.unit_id,  code: stk.unit_code,  name: stk.unit_name,  type: 'unit1', ratio: 1 });
        if (stk.unit2_id) units.push({ id: stk.unit2_id, code: stk.unit2_code, name: stk.unit2_name, type: 'unit2', ratio: parseFloat(stk.unit2_ratio) || 1 });
        if (stk.unit3_id) units.push({ id: stk.unit3_id, code: stk.unit3_code, name: stk.unit3_name, type: 'unit3', ratio: parseFloat(stk.unit3_ratio) || 1 });
        return {
          ...emptyLine(),
          stock_id: stk.id, stock_code: stk.code, stock_name: stk.name, barcode: stk.barcode || '',
          color_name: stk.color_name || '', size_name: stk.size_name || '',
          unit_id: stk.unit_id, unit_type: 'unit1', units,
          quantity: 1, quantity_base: 1,
        };
      }).filter(Boolean);
      // İlk boş satırı kullan
      const merged = [...prev];
      newLines.forEach(nl => {
        const emptyIdx = merged.findIndex(l => !l.stock_id);
        if (emptyIdx >= 0) merged[emptyIdx] = nl;
        else merged.push(nl);
      });
      return merged;
    });
    setShowMultiStock(false);
    // Mevcut'u tetikle
    if (header.from_warehouse_id) {
      setTimeout(() => {
        stockIds.forEach((id) => {
          setLines(prev => {
            const idx = prev.findIndex(l => l.stock_id === id);
            if (idx >= 0) fetchAvailable(header.from_warehouse_id, id, idx);
            return prev;
          });
        });
      }, 0);
    }
  };

  // Kaynak depo değişince tüm satırlardaki "mevcut"u yenile
  useEffect(() => {
    if (!header.from_warehouse_id) return;
    lines.forEach((l, i) => { if (l.stock_id) fetchAvailable(header.from_warehouse_id, l.stock_id, i); });
    // eslint-disable-next-line
  }, [header.from_warehouse_id]);

  const save = async (saveMode = 'close') => {
    if (saveMode && typeof saveMode === 'object') saveMode = 'close';
    setMsg(null);
    if (!header.transfer_date) return setMsg({ type: 'error', text: 'Tarih zorunlu' });
    if (!header.from_warehouse_id) return setMsg({ type: 'error', text: 'Kaynak depo zorunlu' });
    if (!header.to_warehouse_id)   return setMsg({ type: 'error', text: 'Hedef depo zorunlu' });
    if (header.from_warehouse_id === header.to_warehouse_id) return setMsg({ type: 'error', text: 'Kaynak ve hedef depo aynı olamaz' });
    const validLines = lines.filter(l => l.stock_id && parseFloat(l.quantity) > 0);
    if (validLines.length === 0) return setMsg({ type: 'error', text: 'En az bir geçerli kalem girin' });

    setSaving(true);
    try {
      const payload = {
        ...header,
        expected_arrival: header.expected_arrival || null,
        personnel_id: header.personnel_id || null,
        special_code1: header.special_code1 || null,
        special_code2: header.special_code2 || null,
        lines: validLines.map(l => ({
          stock_id: l.stock_id,
          quantity: parseFloat(l.quantity) || 0,
          quantity_base: parseFloat(l.quantity_base) || 0,
          unit_id: l.unit_id || null,
          unit_type: l.unit_type || 'unit1',
          lot_id: l.lot_id || null,
          description: l.description || null,
        })),
      };
      const r = mode === 'edit' && selected
        ? await api.put('/warehouse-transfers/' + selected.id, payload)
        : await api.post('/warehouse-transfers', payload);
      if (r.data.success) {
        setMsg({ type: 'success', text: mode === 'edit' ? `Nakil fişi güncellendi: ${r.data.data.transfer_no}` : `Nakil fişi kaydedildi: ${r.data.data.transfer_no}` });
        await fetchList();
        if (saveMode === 'new' && mode === 'add') {
          // Modal açık kalsın, formu sıfırla ve yeni belge no çek
          try {
            const nn = await api.get('/warehouse-transfers/lookup/next-no');
            if (nn.data.success) setNextNo(nn.data.data.transfer_no);
          } catch {}
          setHeader(emptyHeader());
          setLines([emptyLine()]);
          setBarcodeInput('');
          setTimeout(() => { setMsg({ type: 'success', text: 'Yeni nakil fişi için form sıfırlandı' }); setTimeout(() => setMsg(null), 1500); }, 800);
        } else {
          setTimeout(() => { setShowModal(false); setMsg(null); }, 1200);
        }
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || e.message });
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Bu nakil fişi silinecek. Stok hareketleri geri alınacak. Devam edilsin mi?')) return;
    try {
      await api.delete('/warehouse-transfers/' + id);
      setShowModal(false);
      fetchList();
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || e.message }); }
  };

  const fromWh = (lookups.warehouses || []).find(w => w.id === header.from_warehouse_id);
  const toWh   = (lookups.warehouses || []).find(w => w.id === header.to_warehouse_id);

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Depo Nakil Fişleri"
        count={list.length}
        unit="fiş"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Fiş no, depo, açıklama ara..."
        onRefresh={fetchList}
        refreshing={loading}
        actions={<Button size="sm" onClick={openAdd}>+ Yeni Nakil Fişi</Button>}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="warehouse-transfers-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          data={list}
          emptyText="Nakil fişi bulunamadı"
          rowKey="id"
          onRowClick={r => openView(r.id)}
          columns={[
            { id: 'transfer_no', header: 'Belge No', accessor: 'transfer_no', sortable: true, width: 180,
              cell: r => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.transfer_no}</span> },
            { id: 'transfer_date', header: 'Tarih', accessor: 'transfer_date', sortable: true, width: 100,
              cell: r => fmtDate(r.transfer_date) },
            { id: 'route', header: 'Kaynak → Hedef', accessor: r => `${r.from_warehouse_name} → ${r.to_warehouse_name}`, sortable: true,
              cell: r => (
                <div style={{ fontSize: '0.85rem' }}>
                  <span>{r.from_warehouse_name}</span>
                  <span style={{ color: 'var(--text-tertiary)', margin: '0 6px' }}>→</span>
                  <span style={{ fontWeight: 500 }}>{r.to_warehouse_name}</span>
                </div>
              ) },
            { id: 'personnel', header: 'Personel', accessor: 'personnel_name', sortable: true, width: 140,
              cell: r => r.personnel_name || <span style={{ color: 'var(--text-tertiary)' }}>—</span> },
            { id: 'line_count', header: 'Kalem', accessor: 'line_count', sortable: true, width: 70,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: r => r.line_count || 0 },
            { id: 'total_quantity', header: 'Toplam Mik.', accessor: 'total_quantity', sortable: true, width: 110,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontFamily: 'monospace' },
              cell: r => fmt(r.total_quantity) },
            { id: 'description', header: 'Açıklama', accessor: 'description', sortable: true,
              cell: r => r.description || '-' },
            { id: 'vehicle', header: 'Araç', accessor: 'vehicle_plate', sortable: true, width: 110,
              cell: r => r.vehicle_plate
                ? <div><div style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{r.vehicle_plate}</div>{r.driver_name && <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{r.driver_name}</div>}</div>
                : <span style={{ color: 'var(--text-tertiary)' }}>-</span> },
            { id: 'status', header: 'Durum', accessor: 'status', sortable: true, width: 90,
              cell: r => <span style={{ color: 'var(--success)', fontSize: '0.78rem', fontWeight: 600 }}>✓ Tamam</span> },
          ]}
        />
      </div>

      {showModal && (
        <Modal
          icon={null}
          title={mode === 'add' ? 'Yeni Depo Nakil Fişi' : mode === 'edit' ? 'Depo Nakil Fişi Düzenle' : 'Depo Nakil Fişi'}
          size="xl"
          onClose={() => setShowModal(false)}
          closeOnOverlay={false}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {mode === 'view' && selected && (
                  <Button variant="danger" onClick={() => remove(selected.id)}>🗑 Sil</Button>
                )}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {mode === 'add' && <Button variant="ghost" onClick={() => setShowModal(false)}>Vazgeç</Button>}
                {mode === 'edit' && <Button variant="ghost" onClick={() => setMode('view')}>Vazgeç</Button>}
                {mode === 'view' && selected && (
                  <Button variant="primary" onClick={openEdit}>✎ Düzenle</Button>
                )}
                {mode === 'add' && (
                  <CardSplitButton
                    primaryLabel="✓ Onayla & Kaydet"
                    primaryAction={() => save('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: () => save('new') },
                      { label: 'Kaydet ve Kapat',  icon: '✓', onClick: () => save('close') },
                    ]}
                  />
                )}
                {mode === 'edit' && (
                  <Button variant="primary" onClick={() => save('close')} loading={saving}>✓ Onayla & Kaydet</Button>
                )}
              </div>
            </>
          }
        >
          {mode === 'view' && selected && (
            <CardHero
              icon="📦"
              code={selected.transfer_no}
              title={`${selected.from_warehouse_name} → ${selected.to_warehouse_name}`}
              subtitle={fmtDate(selected.transfer_date)}
              statusBadge={<CardStatusBadge preset="approved" />}
              kpis={[
                { label: 'Kalem', value: (selected.lines || []).length, color: 'info' },
                { label: 'Toplam Miktar', value: fmt((selected.lines || []).reduce((s, l) => s + parseFloat(l.quantity || 0), 0)), color: 'neutral' },
              ]}
            />
          )}

          {msg && (
            <div style={{
              padding: '10px 14px', marginBottom: 12, borderRadius: 8, fontSize: '0.85rem',
              background: msg.type === 'error' ? 'rgba(220,38,38,0.1)' : 'rgba(22,163,74,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : 'var(--success)',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(220,38,38,0.3)' : 'rgba(22,163,74,0.3)'),
            }}>{msg.text}</div>
          )}

          {(() => {
            const isView = mode === 'view';
            // View mode: selected'dan header+lines'ı türet
            const viewHeader = isView && selected ? {
              transfer_date: selected.transfer_date ? selected.transfer_date.slice(0,10) : '',
              from_warehouse_id: selected.from_warehouse_id || '',
              to_warehouse_id: selected.to_warehouse_id || '',
              description: selected.description || '',
              vehicle_plate: selected.vehicle_plate || '',
              driver_name: selected.driver_name || '',
              expected_arrival: selected.expected_arrival ? selected.expected_arrival.slice(0,10) : '',
              personnel_id: selected.personnel_id || '',
              special_code1: selected.special_code1 || '',
              special_code2: selected.special_code2 || '',
            } : null;
            const viewLines = isView && selected ? (selected.lines || []).map(l => {
              const stk = lookups.stocks.find(s => s.id === l.stock_id);
              const units = [];
              if (stk?.unit_id)  units.push({ id: stk.unit_id,  code: stk.unit_code,  name: stk.unit_name,  type: 'unit1', ratio: 1 });
              if (stk?.unit2_id) units.push({ id: stk.unit2_id, code: stk.unit2_code, name: stk.unit2_name, type: 'unit2', ratio: parseFloat(stk.unit2_ratio) || 1 });
              if (stk?.unit3_id) units.push({ id: stk.unit3_id, code: stk.unit3_code, name: stk.unit3_name, type: 'unit3', ratio: parseFloat(stk.unit3_ratio) || 1 });
              return {
                stock_id: l.stock_id,
                stock_code: l.stock_code || stk?.code || '',
                stock_name: l.stock_name || stk?.name || '',
                barcode: l.stock_barcode || stk?.barcode || '',
                color_name: stk?.color_name || '',
                size_name: stk?.size_name || '',
                quantity: l.quantity,
                quantity_base: l.quantity_base || l.quantity,
                unit_id: l.unit_id,
                unit_type: l.unit_type || 'unit1',
                units,
                description: l.description || '',
                available: null,
              };
            }) : null;
            return (
              <AddForm
                header={isView ? viewHeader : header} setHeader={setHeader}
                lines={isView ? viewLines : lines} updateLine={updateLine} addLine={addLine} removeLine={removeLine}
                lookups={lookups}
                fromWh={isView ? (lookups.warehouses || []).find(w => w.id === selected?.from_warehouse_id) : fromWh}
                toWh={isView ? (lookups.warehouses || []).find(w => w.id === selected?.to_warehouse_id) : toWh}
                nextNo={isView ? selected?.transfer_no : (mode === 'edit' ? selected?.transfer_no : nextNo)}
                barcodeInput={barcodeInput} setBarcodeInput={setBarcodeInput}
                onBarcodeScan={handleBarcodeScan}
                onOpenMultiStock={() => setShowMultiStock(true)}
                colorSizeEnabled={colorSizeEnabled}
                readOnly={isView}
              />
            );
          })()}
        </Modal>
      )}

      {showMultiStock && (
        <MultiStockModal
          stocks={lookups.stocks || []}
          alreadySelected={new Set(lines.filter(l => l.stock_id).map(l => l.stock_id))}
          onClose={() => setShowMultiStock(false)}
          onConfirm={addMultipleStocks}
          colorSizeEnabled={colorSizeEnabled}
        />
      )}
    </div>
  );
}

function AddForm({ header, setHeader, lines, updateLine, addLine, removeLine, lookups, fromWh, toWh,
                   nextNo, barcodeInput, setBarcodeInput, onBarcodeScan, onOpenMultiStock,
                   colorSizeEnabled = false, readOnly = false }) {
  const h = (k, v) => { if (!readOnly) setHeader(prev => ({ ...prev, [k]: v })); };
  const linesLocked = !readOnly && (!header.from_warehouse_id || !header.to_warehouse_id);
  const lockMsg = 'Önce kaynak ve hedef depoyu seçin';
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Üst: belge no + tarih + açıklama + personel */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <FormField label="Belge No (otomatik)">
          <input value={nextNo || '...'} readOnly disabled
            style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--accent-primary)', cursor: 'not-allowed' }} />
        </FormField>
        <FormField label="Tarih *">
          <input type="date" value={header.transfer_date} onChange={e => h('transfer_date', e.target.value)} disabled={readOnly} />
        </FormField>
        <FormField label="Personel (ops.)">
          <SearchableSelect
            value={header.personnel_id}
            onChange={v => h('personnel_id', v)}
            options={lookups.personnel || []}
            getValue={p => p.id}
            getLabel={p => (p.code ? p.code + ' - ' : '') + p.full_name}
            placeholder="Personel seç..."
            allowClear
            disabled={readOnly}
          />
        </FormField>
        <FormField label="Özel Kod 1">
          <SearchableSelect
            value={header.special_code1}
            onChange={v => h('special_code1', v)}
            options={lookups.specialCodes1 || []}
            getValue={s => s.code}
            getLabel={s => s.code + ' - ' + s.name}
            placeholder="Özel Kod 1..."
            allowClear
            disabled={readOnly}
          />
        </FormField>
        <FormField label="Özel Kod 2">
          <SearchableSelect
            value={header.special_code2}
            onChange={v => h('special_code2', v)}
            options={lookups.specialCodes2 || []}
            getValue={s => s.code}
            getLabel={s => s.code + ' - ' + s.name}
            placeholder="Özel Kod 2..."
            allowClear
            disabled={readOnly}
          />
        </FormField>
        <div style={{ gridColumn: '1 / -1' }}>
          <FormField label="Açıklama">
            <input value={header.description} onChange={e => h('description', e.target.value)} placeholder="Nakil açıklaması..." disabled={readOnly} />
          </FormField>
        </div>
      </div>

      {/* Kaynak ↔ Hedef */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'center' }}>
        <WarehouseCard label="Kaynak Depo *" warehouseList={lookups.warehouses || []} value={header.from_warehouse_id}
          onChange={v => h('from_warehouse_id', v)} highlight={fromWh} disabled={readOnly} />
        <div style={{ fontSize: '1.5rem', color: 'var(--accent-primary)' }}>→</div>
        <WarehouseCard label="Hedef Depo *" warehouseList={(lookups.warehouses || []).filter(w => w.id !== header.from_warehouse_id)}
          value={header.to_warehouse_id} onChange={v => h('to_warehouse_id', v)} highlight={toWh} disabled={readOnly} />
      </div>

      {/* Araç / şoför / varış */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <FormField label="Araç Plaka (ops.)">
          <input value={header.vehicle_plate} onChange={e => h('vehicle_plate', e.target.value)} placeholder="34 ABC 123" disabled={readOnly} />
        </FormField>
        <FormField label="Şoför (ops.)">
          <input value={header.driver_name} onChange={e => h('driver_name', e.target.value)} placeholder="Şoför adı" disabled={readOnly} />
        </FormField>
        <FormField label="Tahmini Varış (ops.)">
          <input type="date" value={header.expected_arrival} onChange={e => h('expected_arrival', e.target.value)} disabled={readOnly} />
        </FormField>
      </div>

      {/* Kalemler — satış faturası stili */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h4 style={{ fontSize: '0.9rem', fontWeight: 600, margin: 0 }}>Kalemler</h4>
        </div>

        {/* Araç çubuğu: barkod + yeni stok + çoklu stok seç + seri listesi (sadece add/edit) */}
        {!readOnly && <div style={{ display: 'flex', gap: 8, padding: '10px 14px', marginBottom: 8, background: 'transparent', alignItems: 'center', flexWrap: 'wrap', opacity: linesLocked ? 0.5 : 1, pointerEvents: linesLocked ? 'none' : 'auto' }}
             title={linesLocked ? lockMsg : ''}>
          <div style={{ position: 'relative', display: 'flex', gap: 4, alignItems: 'stretch' }}>
            <div style={{ position: 'relative' }}>
              <input
                value={barcodeInput}
                onChange={e => setBarcodeInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onBarcodeScan(); } }}
                placeholder="Barkod okut / Enter"
                disabled={linesLocked}
                style={{
                  width: 200, height: 34, padding: '0 10px 0 36px', borderRadius: 8,
                  border: '1px solid var(--border-input)',
                  background: 'var(--bg-input)', color: 'var(--text-primary)',
                  fontSize: '0.85rem', fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box',
                }}
              />
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', opacity: 0.4, pointerEvents: 'none' }}>
                <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="7" y1="8" x2="7" y2="16"/><line x1="11" y1="8" x2="11" y2="16"/><line x1="15" y1="8" x2="15" y2="16"/>
              </svg>
            </div>
            <button type="button" onClick={onBarcodeScan} title="Kamera ile tara" disabled={linesLocked}
              style={{
                height: 34, width: 34, padding: 0,
                background: 'var(--bg-secondary)', border: '1px solid var(--border-input)',
                borderRadius: 8, cursor: linesLocked ? 'not-allowed' : 'pointer', fontSize: '0.95rem', lineHeight: 1, boxSizing: 'border-box',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>📷</button>
          </div>
          <button className="btn-ghost" onClick={() => window.open('/?page=stocks', '_blank')} disabled={linesLocked}>+ Yeni Stok</button>
          <button className="btn-ghost" onClick={onOpenMultiStock} disabled={linesLocked}>📋 Çoklu Stok Seç</button>
          <button className="btn-ghost" onClick={onOpenMultiStock} disabled={linesLocked}>📦 Seri Listesi</button>
        </div>}

        {!readOnly && linesLocked && (
          <div style={{
            padding: '10px 14px', marginBottom: 8, borderRadius: 8,
            background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
            color: 'var(--warning)', fontSize: '0.82rem',
          }}>
            ⚠ {lockMsg} — kalem girişi için <strong>Kaynak Depo</strong> ve <strong>Hedef Depo</strong> zorunludur.
          </div>
        )}

        <div style={{ overflowX: 'auto', opacity: linesLocked ? 0.5 : 1, pointerEvents: linesLocked ? 'none' : 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 820 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-secondary)', color: 'var(--text-tertiary)', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <th style={{ width: 28, padding: '4px' }}>#</th>
                <th style={{ width: 28, padding: '4px' }}></th>
                <th style={{ textAlign: 'left', padding: '6px 4px', minWidth: 340 }}>Stok / Açıklama</th>
                <th style={{ textAlign: 'right', padding: '6px 4px', width: 80 }}>Birim Mkt</th>
                <th style={{ textAlign: 'left', padding: '6px 4px', width: 130 }}>Birim</th>
                <th style={{ textAlign: 'right', padding: '6px 4px', width: 90 }}>Miktar</th>
                <th style={{ textAlign: 'right', padding: '6px 4px', width: 90 }}>Mevcut</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((ln, idx) => {
                const is2 = { width: '100%', padding: '4px', background: 'var(--bg-input)', border: '1px solid var(--border-input)', borderRadius: 4, color: 'var(--text-primary)', fontSize: '0.8rem' };
                const insufficient = ln.available != null && parseFloat(ln.quantity_base || 0) > parseFloat(ln.available);
                return (
                  <React.Fragment key={idx}>
                    <tr style={{ borderBottom: 'none' }}>
                      <td style={{ padding: '2px', width: 28, textAlign: 'center', verticalAlign: 'top', paddingTop: 14, color: 'var(--text-tertiary)', fontSize: '0.78rem' }}>{idx + 1}</td>
                      <td style={{ padding: '2px', width: 28, verticalAlign: 'top', paddingTop: 10 }}>
                        {!readOnly && lines.length > 1 && (
                          <button onClick={() => removeLine(idx)}
                            style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '1rem' }}>×</button>
                        )}
                      </td>
                      <td rowSpan={2} style={{ padding: '4px', verticalAlign: 'top', minWidth: 340 }}>
                        <div style={{
                          padding: '7px 9px',
                          background: 'var(--bg-secondary)',
                          border: ln.stock_id ? '1px solid var(--border-secondary)' : '1px dashed var(--border-input)',
                          borderRadius: 8, minHeight: 54,
                        }}>
                          <select
                            value={ln.stock_id}
                            onChange={e => updateLine(idx, 'stock_id', e.target.value)}
                            disabled={readOnly}
                            style={{ ...is2, fontWeight: ln.stock_id ? 600 : 400, fontSize: '0.82rem', padding: '5px 6px' }}>
                            <option value="">Stok seç...</option>
                            {(lookups.stocks || []).map(s => {
                              const extras = colorSizeEnabled ? [s.color_name, s.size_name].filter(Boolean).join(' / ') : '';
                              return <option key={s.id} value={s.id}>{s.name}{extras ? ` — ${extras}` : ''}</option>;
                            })}
                          </select>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 5, fontSize: '0.7rem', color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                            {ln.stock_id ? (
                              <>
                                {ln.stock_code && <span style={{ fontFamily: 'monospace', color: 'var(--text-secondary)', fontWeight: 600 }}>{ln.stock_code}</span>}
                                {ln.barcode && (<><span style={{ opacity: 0.5 }}>·</span><span style={{ fontFamily: 'monospace' }}>{ln.barcode}</span></>)}
                                {ln.color_name && (<><span style={{ opacity: 0.5 }}>·</span><strong style={{ color: 'var(--text-secondary)' }}>{ln.color_name}</strong></>)}
                                {ln.size_name && (<><span style={{ opacity: 0.5 }}>·</span><strong style={{ color: 'var(--text-secondary)' }}>{ln.size_name}</strong></>)}
                                {!ln.stock_code && !ln.barcode && !ln.color_name && !ln.size_name &&
                                  <span style={{ fontStyle: 'italic', opacity: 0.7 }}>— ek bilgi yok —</span>}
                              </>
                            ) : (
                              <span style={{ fontStyle: 'italic', opacity: 0.7 }}>Kod / barkod / renk / beden seçilecek stok ile gelir</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '2px', width: 80 }}>
                        <input value={ln.quantity_base || ''} disabled
                          style={{ ...is2, textAlign: 'right', color: 'var(--accent-primary)', fontWeight: 600 }} />
                      </td>
                      <td style={{ padding: '2px', width: 130 }}>
                        <select value={ln.unit_type || 'unit1'} onChange={e => updateLine(idx, 'unit_type', e.target.value)} disabled={readOnly} style={is2}>
                          {(ln.units || []).map(u => <option key={u.type} value={u.type}>{u.name} (x{u.ratio})</option>)}
                          {(!ln.units || ln.units.length === 0) && <option value="unit1">Adet</option>}
                        </select>
                      </td>
                      <td style={{ padding: '2px', width: 90 }}>
                        <input type="number" step="0.001" value={ln.quantity}
                          onChange={e => updateLine(idx, 'quantity', e.target.value)}
                          disabled={readOnly}
                          style={{ ...is2, textAlign: 'right',
                                   borderColor: insufficient ? 'var(--danger)' : undefined }} />
                      </td>
                      <td style={{ padding: '2px', width: 90, textAlign: 'right', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        {ln.available != null
                          ? <span style={{ color: insufficient ? 'var(--danger)' : 'var(--success)', fontWeight: 600 }}>{fmt(ln.available)}</span>
                          : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                      <td colSpan={2}></td>
                      <td colSpan={4} style={{ padding: '2px 4px 8px' }}>
                        <input value={ln.description}
                          onChange={e => updateLine(idx, 'description', e.target.value)}
                          placeholder="Açıklama (opsiyonel)..."
                          disabled={readOnly}
                          style={{ ...is2, fontSize: '0.78rem', background: 'transparent', border: '1px dashed var(--border-input)' }} />
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* + Satır Ekle (sol) + Toplam özet (sağ) */}
        {(() => {
          const validLines = lines.filter(l => l.stock_id && parseFloat(l.quantity || 0) > 0);
          const totalQty = validLines.reduce((s, l) => s + (parseFloat(l.quantity) || 0), 0);
          const totalQtyBase = validLines.reduce((s, l) => s + (parseFloat(l.quantity_base) || 0), 0);
          return (
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              {!readOnly ? (
                <Button size="sm" variant="primary" onClick={addLine} disabled={linesLocked} title={linesLocked ? lockMsg : ''}>+ Satır Ekle</Button>
              ) : <div />}
              <div style={{
                padding: '8px 16px',
                background: 'var(--bg-secondary)', borderRadius: 8,
                fontSize: '0.82rem',
              }}>
                <span style={{ color: 'var(--text-tertiary)' }}>Toplam Miktar: </span>
                <strong style={{ fontFamily: 'monospace' }}>{fmt(totalQty)}</strong>
              </div>
            </div>
          );
        })()}
      </div>

      <div style={{ padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>
        💡 Kaydet'e basıldığında kaynak depodan otomatik çıkış, hedef depoya otomatik giriş yapılır. Muhasebe kaydı yazılmaz (depo içi hareket).
      </div>
    </div>
  );
}

function WarehouseCard({ label, warehouseList, value, onChange, highlight, disabled = false }) {
  return (
    <FormField label={label}>
      <SearchableSelect
        value={value}
        onChange={onChange}
        options={warehouseList}
        getValue={w => w.id}
        getLabel={w => `${w.code} - ${w.name}`}
        placeholder="Depo seç..."
        disabled={disabled}
      />
      {highlight && (
        <div style={{ marginTop: 6, padding: '6px 10px', background: 'var(--bg-secondary)', borderRadius: 6, fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
          Kod: <strong>{highlight.code}</strong>
        </div>
      )}
    </FormField>
  );
}

function ViewForm({ transfer }) {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: 10 }}>
        <Info label="Kaynak Depo" value={transfer.from_warehouse_name} />
        <Info label="Hedef Depo"  value={transfer.to_warehouse_name} />
        <Info label="Tarih" value={fmtDate(transfer.transfer_date)} />
        {transfer.personnel_name && <Info label="Personel" value={transfer.personnel_name} />}
        {transfer.special_code1 && <Info label="Özel Kod 1" value={transfer.special_code1} />}
        {transfer.special_code2 && <Info label="Özel Kod 2" value={transfer.special_code2} />}
        {transfer.expected_arrival && <Info label="Tahmini Varış" value={fmtDate(transfer.expected_arrival)} />}
        {transfer.vehicle_plate && <Info label="Araç" value={transfer.vehicle_plate} />}
        {transfer.driver_name && <Info label="Şoför" value={transfer.driver_name} />}
        <Info label="Düzenleyen" value={transfer.created_by_name || '-'} />
      </div>
      {transfer.description && (
        <div style={{ padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.85rem' }}>
          <strong>Açıklama:</strong> {transfer.description}
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-secondary)', color: 'var(--text-tertiary)', fontSize: '0.75rem' }}>
            <th style={{ textAlign: 'left',  padding: '6px 4px', width: 40 }}>#</th>
            <th style={{ textAlign: 'left',  padding: '6px 4px' }}>Stok</th>
            <th style={{ textAlign: 'right', padding: '6px 4px', width: 90 }}>Birim Mkt</th>
            <th style={{ textAlign: 'right', padding: '6px 4px', width: 120 }}>Miktar</th>
            <th style={{ textAlign: 'left',  padding: '6px 4px' }}>Açıklama</th>
          </tr>
        </thead>
        <tbody>
          {(transfer.lines || []).map(l => (
            <tr key={l.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
              <td style={{ padding: 4, color: 'var(--text-tertiary)' }}>{l.line_no}</td>
              <td style={{ padding: 4 }}>
                <div style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>{l.stock_code}{l.stock_barcode ? ' · ' + l.stock_barcode : ''}</div>
                <div>{l.stock_name}</div>
              </td>
              <td style={{ padding: 4, textAlign: 'right', fontFamily: 'monospace', color: 'var(--accent-primary)', fontWeight: 600 }}>{fmt(l.quantity_base || l.quantity)}</td>
              <td style={{ padding: 4, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                {fmt(l.quantity)} {l.unit_name || l.unit_code || ''}
              </td>
              <td style={{ padding: 4 }}>{l.description || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: '0.88rem' }}>{value}</div>
    </div>
  );
}

function MultiStockModal({ stocks, alreadySelected, onClose, onConfirm, colorSizeEnabled = false }) {
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(new Set());

  const filtered = stocks.filter(s => {
    if (alreadySelected.has(s.id)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.name || '').toLowerCase().includes(q)
        || (s.code || '').toLowerCase().includes(q)
        || (s.barcode || '').toLowerCase().includes(q);
  });

  const toggle = (id) => setPicked(prev => {
    const ns = new Set(prev);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    return ns;
  });

  const toggleAll = () => {
    if (picked.size === filtered.length) setPicked(new Set());
    else setPicked(new Set(filtered.map(s => s.id)));
  };

  return (
    <Modal
      title={`Çoklu Stok Seç (${picked.size} seçili)`}
      size="xl"
      onClose={onClose}
      closeOnOverlay={false}
      primaryAction={{ label: `Ekle (${picked.size})`, onClick: () => onConfirm(Array.from(picked)), disabled: picked.size === 0 }}
      cancelAction={{ label: 'Vazgeç', onClick: onClose }}
    >
      <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Stok ara (ad, kod, barkod)..."
          style={{ flex: 1, padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
        />
        <Button size="sm" variant="ghost" onClick={toggleAll}>
          {picked.size === filtered.length && filtered.length > 0 ? 'Tümünü Kaldır' : 'Tümünü Seç'}
        </Button>
      </div>
      <div style={{ maxHeight: 500, overflowY: 'auto', border: '1px solid var(--border-secondary)', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-primary)', zIndex: 1 }}>
            <tr style={{ borderBottom: '1px solid var(--border-secondary)', color: 'var(--text-tertiary)', fontSize: '0.74rem' }}>
              <th style={{ width: 40, padding: '8px 4px' }}></th>
              <th style={{ textAlign: 'left', padding: '8px 8px', width: 130 }}>Kod</th>
              <th style={{ textAlign: 'left', padding: '8px 8px' }}>Ad</th>
              {colorSizeEnabled && <th style={{ textAlign: 'left', padding: '8px 8px', width: 90 }}>Renk</th>}
              {colorSizeEnabled && <th style={{ textAlign: 'left', padding: '8px 8px', width: 80 }}>Beden</th>}
              <th style={{ textAlign: 'left', padding: '8px 8px', width: 140 }}>Barkod</th>
              <th style={{ textAlign: 'left', padding: '8px 8px', width: 100 }}>Birim</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={colorSizeEnabled ? 7 : 5} style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}>
                {stocks.length === 0 ? 'Stok yok' : 'Eşleşen stok bulunamadı (veya tümü zaten seçili)'}
              </td></tr>
            )}
            {filtered.map(s => (
              <tr key={s.id} onClick={() => toggle(s.id)}
                style={{ borderBottom: '1px solid var(--border-secondary)', cursor: 'pointer',
                         background: picked.has(s.id) ? 'rgba(59,130,246,0.08)' : 'transparent' }}>
                <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                  <input type="checkbox" checked={picked.has(s.id)} readOnly onClick={e => e.stopPropagation()} onChange={() => toggle(s.id)} />
                </td>
                <td style={{ padding: '8px', fontFamily: 'monospace', color: 'var(--text-tertiary)' }}>{s.code}</td>
                <td style={{ padding: '8px', fontWeight: 500 }}>{s.name}</td>
                {colorSizeEnabled && <td style={{ padding: '8px', fontSize: '0.78rem' }}>{s.color_name || '-'}</td>}
                {colorSizeEnabled && <td style={{ padding: '8px', fontSize: '0.78rem' }}>{s.size_name || '-'}</td>}
                <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>{s.barcode || '-'}</td>
                <td style={{ padding: '8px' }}>{s.unit_code || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
